<template>
  <div class="checkout lg:px-3">
    <QuoteLinkIssueModal />
    <h1
      v-if="isHeaderVisible"
      class="text-center pt-10 pb-7 lg:text-left"
    >
      {{ currentStepHeader }}
    </h1>
    <CustomerTypeTabs
      v-if="isCustomerTypeTabsVisible"
      :class="leftSideClasses"
    />
    <div
      v-if="isStepsVisible"
      class="flex flex-row"
    >
      <div class="w-full lg:w-1/2 lg:pr-9">
        <CustomSteps
          :active="currentStepIndex"
          :steps="stepsArray"
        />
      </div>
    </div>
    <div
      class="flex flex-col mb-6"
      :class="isThankYouPage ? '' : 'lg:flex-row'"
    >
      <transition name="sf-fade">
        <div
          v-if="!isCheckoutCartHidden"
          class="order-1 w-1/2"
        >
          <ClientDesktopOnly>
            <template v-if="totalItems">
              <CartCheckout
                data-testid="checkoutCartDesktop"
              />
              <CheckoutSecurePayment
                :url="securePaymentInfoUrl"
              />
            </template>
          </ClientDesktopOnly>
          <SkeletonLoader
            v-if="!cartIsLoaded"
            height="500px"
            class="hidden lg:block pb-6 w-full"
          />
        </div>
      </transition>
      <div
        :class="leftSideClasses"
      >
        <ClientMobileOnly v-if="!isCheckoutCartHidden">
          <CartCheckout
            v-if="totalItems"
          />
          <SkeletonLoader
            v-if="!cartIsLoaded"
            height="150px"
            class="pb-12 w-full"
          />
        </ClientMobileOnly>
        <div :class="!isThankYouPage && 'p-5 lg:px-0 lg:p-0'">
          <NuxtPage
            v-if="totalItems || isCheckoutCartHidden"
          />
          <CheckoutSkeletons
            v-if="!cartIsLoaded"
          />
          <CheckoutSecurePayment
            v-if="!isCheckoutCartHidden && totalItems"
            class="lg:hidden"
            :url="securePaymentInfoUrl"
          />
        </div>
      </div>
    </div>
  </div>
</template>
<script lang="ts">
import { ROUTES } from '~~/constants/routes';
import { generateMetaTitleWithCTFormat } from '~~/helpers/commercetools/meta/generateMetaTitle';
import CheckoutSkeletons from '~/components/molecules/Checkout/CheckoutSkeletons/CheckoutSkeletons.vue';
import QuoteLinkIssueModal from '~/components/molecules/Cart/QuoteLinkIssueModal.vue';
import { useUser, cartGetters } from '~/utils/ct';

definePageMeta({
  layout: 'base-layout',
  pageTransition: false,
  layoutTransition: false,
  middleware: [
    'redirect-extra-guarantee-cart',
    'checkout-index-redirect',
  ],
});

export default defineComponent({
  name: 'Checkout',
  components: {
    CheckoutSkeletons,
    QuoteLinkIssueModal,
    CustomerTypeTabs: () => import(/* webpackChunkName: "CustomerTypeTabs" */
      '~/components/molecules/Checkout/CustomerTypeTabs/CustomerTypeTabs.vue'),
    CartCheckout: () => import(/* webpackChunkName: "CartCheckout" */
      '~/components/organisms/Cart/CartCheckout.vue'),
    CheckoutSecurePayment: () => import(/* webpackChunkName: "CheckoutSecurePayment" */
      '~/components/organisms/Checkout/CheckoutSecurePayment/CheckoutSecurePayment.vue'),
    CustomSteps: () => import(/* webpackChunkName: "CustomSteps" */
      '~/components/molecules/Checkout/CustomSteps/CustomSteps.vue'),
  },
  setup() {

    const i18n = useI18n();
    const route = useRoute();
    const { getAdjustedSlug } = useRouteExtended();
    const {
      isOnAddressesPage,
      isOnPaymentPage,
      isThankYouPage,
      isCheckoutLogin,
      isCheckoutCreateAccount,
    } = useIsPage();
    const { callPageViewEvent } = usePageViewEvent();
    const { isAuthenticated } = useUser();
    const { resetPaymentErrors } = useCustomPayment();

    const { cart, isLoaded: cartIsLoaded } = useCartExtended();

    const isHeaderVisible = computed(() =>
      !isThankYouPage.value && !isCheckoutCreateAccount.value);
    const isCustomerTypeTabsVisible = computed(() =>
      !isAuthenticated.value && (isOnAddressesPage.value || isCheckoutLogin.value));
    const isStepsVisible = computed(() =>
      !isThankYouPage.value && !isCheckoutLogin.value &&
      !isCheckoutCreateAccount.value && !isCustomerTypeTabsVisible.value);

    const securePaymentInfoUrl = computed(() => getAdjustedSlug(ROUTES.PAYMENT_METHODS));

    /**
     * Object with all the steps visible in the checkout bar
     *  Keys: SLUG from ROUTES
     *  Values: name of SfStep
     */
    const STEPS: {[p: string]: string} = {
      ['CART']: i18n.t('Cart'),
      [getAdjustedSlug(ROUTES.CHECKOUT_ADDRESSES)]: i18n.t('Shipping and Billing Addresses'),
      [getAdjustedSlug(ROUTES.CHECKOUT_PAYMENT)]: i18n.t('Payment'),
    };
    /**
     * Object with all the available checkout headers
     *  Keys: SLUG from ROUTES
     *  Values: header
     */
    const headerSteps = {
      ...STEPS,
      [getAdjustedSlug(ROUTES.CHECKOUT_LOGIN)]: i18n.t('Login'),
      ['CART']: i18n.t('Your cart'),
      [getAdjustedSlug(ROUTES.THANK_YOU)]: i18n.t('Thanks for your order'),
    };

    const currentStepIndex = computed(() => Object.keys(STEPS).findIndex((s) => s === route.path));

    const leftSideClasses = computed(() => isCheckoutCartHidden.value
      ? 'w-full'
      : 'lg:w-1/2 lg:pr-9');

    const totalItems = computed(() => cartGetters.getTotalItems(cart.value));

    const currentStepHeader = computed(() => headerSteps[route.path]);
    const metaTitle = computed(() =>
      currentStepHeader.value && generateMetaTitleWithCTFormat({
        title: currentStepHeader.value, i18n,
      }),
    );
    useMetaExtended({
      metaTitle,
      isPageIndexed: ref(false),
    });

    const trackCheckoutPageView = () => callPageViewEvent({ pageTitle: metaTitle.value });

    onMounted(() => {

      resetPaymentErrors();
      trackCheckoutPageView();

    });

    watch([isOnAddressesPage, isOnPaymentPage], ([isAddressesStep, isPaymentStep]) => {

      if (isAddressesStep || isPaymentStep) {

        trackCheckoutPageView();

      }

    });

    const isCheckoutCartHidden = computed(() =>
      isThankYouPage.value || isCheckoutLogin.value || isCheckoutCreateAccount.value);

    const stepsArray = computed(() => Object.values(STEPS));

    return {
      isCustomerTypeTabsVisible,
      isCheckoutCartHidden,
      totalItems,
      currentStepIndex,
      isThankYouPage,
      currentStepHeader,
      isHeaderVisible,
      isStepsVisible,
      leftSideClasses,
      securePaymentInfoUrl,
      stepsArray,
      cartIsLoaded,
    };

  },
});
</script>

<style lang="scss" scoped>
@use "~/assets/scss/helpers/_breakpoints" as breakpoints;

:deep(*) {
  .checkout {
    &__steps {
      --steps-content-padding: var(--spacer-xl) var(--spacer-base) 0;
      --steps-step-color: var(--_c-dark-primary);
      --steps-progress-background: var(--c-primary-80);
      --steps-step-cursor: default;

      .sf-steps {
        &__content {
          padding: 0;
        }

        @include breakpoints.for-desktop {
          &__content {
            padding: var(--spacer-xs) 0;
          }
        }

        &__step.is-done {
          --steps-step-cursor: default;

          color: var(--c-primary-80);
        }
      }
    }

    &__action {
      @include breakpoints.for-desktop {
        flex: 0 0 100%;
        display: flex;
      }
    }
  }
}
</style>
